import { useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Sparkles, Smile, ArrowRight, Star, Lightbulb, Trophy, HeartHandshake, Rocket, Crown, Swords, Flame, Feather, Scale, Sunrise, Shield, Landmark, Heart, Music, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import CTA from "../components/sections/CTA";
import { riddlesPool, jokesPool, brainGamesPool } from "../mock/knowledgeHouse";

const littleStories = [
  { id: 1, title: "Little Krishna Shares Butter", value: "Sharing", monogram: "कृ", palette: "from-sky-400 to-blue-500", accent: "bg-sky-100 text-sky-800",
    illustration: "https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/0ih7vror_907750e8-4829-467f-a4c8-93eb64c268be.png",
    story: "Little Krishna loved butter very much. One day, he saw his friends looking hungry. Krishna smiled and shared his butter with everyone. All the children laughed, ate together, and became happy.",
    moral: "Sharing makes everyone happy." },
  { id: 2, title: "Lord Rama Always Told the Truth", value: "Honesty", monogram: "रा", palette: "from-emerald-400 to-teal-500", accent: "bg-emerald-100 text-emerald-800",
    illustration: "https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/9uddnnfb_f5fcadb3-45a4-4a86-81e5-41a34f1a796f.png",
    story: "Young Rama always spoke the truth. Even when it was difficult, he chose honesty. Everyone trusted him because he was truthful and kind.",
    moral: "Always speak the truth." },
  { id: 3, title: "Hanuman's Big Jump", value: "Confidence", monogram: "ह", palette: "from-orange-400 to-red-500", accent: "bg-orange-100 text-orange-800",
    story: "Little Hanuman believed he could do great things. With courage in his heart, he made a giant leap across the sea. He never gave up.",
    moral: "Believe in yourself." },
  { id: 4, title: "Little Buddha and the Bird", value: "Kindness", monogram: "बु", palette: "from-yellow-400 to-amber-500", accent: "bg-yellow-100 text-yellow-800",
    story: "Prince Siddhartha found a little bird with an injured wing. He gently cared for it until it could fly again. The bird flew happily into the sky.",
    moral: "Be kind to every living being." },
  { id: 5, title: "Gandhi Ji and the Pencil", value: "Carefulness", monogram: "गा", palette: "from-lime-400 to-green-500", accent: "bg-lime-100 text-lime-800",
    story: "One day, Gandhi Ji searched for a tiny pencil because he did not want to waste it. He believed every little thing had value.",
    moral: "Do not waste anything." },
  { id: 6, title: "Swami Vivekananda Loved Learning", value: "Curiosity", monogram: "वि", palette: "from-amber-400 to-orange-500", accent: "bg-amber-100 text-amber-800",
    story: "Young Narendra asked many questions. He loved reading books and learning new things every day. His teachers were happy to see his curiosity.",
    moral: "Keep learning every day." },
  { id: 7, title: "Rani Lakshmibai Was Brave", value: "Courage", monogram: "ल", palette: "from-rose-400 to-pink-600", accent: "bg-rose-100 text-rose-800",
    story: "The little queen was never afraid of difficulties. She protected her people with courage and determination.",
    moral: "Be brave and never give up." },
  { id: 8, title: "Shivaji Maharaj Respected Everyone", value: "Respect", monogram: "श", palette: "from-orange-500 to-red-600", accent: "bg-orange-100 text-orange-800",
    story: "Shivaji Maharaj always respected elders, women, soldiers, and common people. Everyone respected him in return.",
    moral: "Respect everyone." },
  { id: 9, title: "Sant Kabir Loved Everyone", value: "Love", monogram: "क", palette: "from-fuchsia-400 to-pink-500", accent: "bg-fuchsia-100 text-fuchsia-800",
    story: "Kabir taught that all people are equal. He smiled, helped others, and spread love wherever he went.",
    moral: "Love everyone." },
  { id: 10, title: "Guru Nanak Shared His Food", value: "Generosity", monogram: "ना", palette: "from-cyan-400 to-teal-500", accent: "bg-cyan-100 text-cyan-800",
    story: "Guru Nanak saw hungry people and shared his food with them. Everyone ate together happily.",
    moral: "Sharing is caring." },
  { id: 11, title: "APJ Abdul Kalam Dreamed Big", value: "Dream Big", monogram: "अ", palette: "from-indigo-400 to-sky-600", accent: "bg-indigo-100 text-indigo-800",
    story: "Little Abdul loved watching birds fly. He studied hard and worked with dedication. One day, he helped India reach the skies.",
    moral: "Dream big and work hard." },
  { id: 12, title: "Chanakya Used His Wisdom", value: "Thinking", monogram: "चा", palette: "from-violet-400 to-purple-600", accent: "bg-violet-100 text-violet-800",
    story: "Chanakya always solved problems calmly. He believed that thinking carefully helps us make good choices.",
    moral: "Think before you act." },
  { id: 13, title: "Mirabai Sang with Love", value: "Devotion", monogram: "मी", palette: "from-pink-400 to-rose-500", accent: "bg-pink-100 text-pink-800",
    story: "Little Mirabai loved singing beautiful songs with joy. Her songs spread happiness wherever she went.",
    moral: "Sing with love and keep a happy heart." },
  { id: 14, title: "Sant Tulsidas Helped Others", value: "Helping", monogram: "तु", palette: "from-teal-400 to-emerald-500", accent: "bg-teal-100 text-teal-800",
    story: "Tulsidas always helped people who needed support. His kind words made everyone smile.",
    moral: "Help others whenever you can." },
  { id: 15, title: "Mother Teresa Cared for Everyone", value: "Compassion", monogram: "त", palette: "from-blue-400 to-indigo-500", accent: "bg-blue-100 text-blue-800",
    story: "Mother Teresa cared for sick and lonely people with love. She believed every person deserved kindness.",
    moral: "A loving heart is the greatest gift." },
];

const pledgeLines = [
  "I will smile.",
  "I will share.",
  "I will help.",
  "I will speak the truth.",
  "I will respect everyone.",
  "I will love my parents, teachers, nature, and my country."
];

const leaders = [
  {
    id: "shivaji-maharaj",
    name: "Chhatrapati Shivaji Maharaj",
    tag: "Warrior • Visionary • Nation Builder",
    era: "1630 – 1680",
    icon: Crown,
    palette: "from-orange-500 to-red-600",
    accent: "bg-orange-100 text-orange-800",
    monogram: "श",
    virtue: "Courage & Righteous Leadership",
    story:
      "When Shivaji Maharaj was just a small boy, his mother Jijabai would sit by his side and tell him stories from the Ramayana and Mahabharata. She planted a single thought in his heart — that dharma must be protected. Even before he grew tall enough to hold a sword, he had already decided to build a kingdom rooted in justice, protection of the weak, and love for Bharat.",
    lesson:
      "A great life begins in the lap of a mother's stories. Children who hear tales of courage, kindness and duty grow up to become citizens who make Bharat proud."
  },
  {
    id: "swami-vivekananda",
    name: "Swami Vivekananda",
    tag: "Monk • Youth Icon • Torchbearer of Vedanta",
    era: "1863 – 1902",
    icon: Flame,
    palette: "from-amber-500 to-orange-600",
    accent: "bg-amber-100 text-amber-800",
    monogram: "वि",
    virtue: "Confidence & Fearlessness",
    story:
      "As a young boy named Narendra, he was full of curiosity and questions. He would ask his teachers, his parents and even sadhus — 'Have you seen God?' Most people had no answer. But this fire of enquiry never left him. It carried him all the way to Chicago in 1893, where his opening words — 'Sisters and Brothers of America' — brought a hall of 7,000 to its feet.",
    lesson:
      "Never stop asking questions. A curious mind is the most powerful gift a child can carry into the world."
  },
  {
    id: "apj-abdul-kalam",
    name: "Dr. A.P.J. Abdul Kalam",
    tag: "People's President • Missile Man • Teacher First",
    era: "1931 – 2015",
    icon: Rocket,
    palette: "from-sky-500 to-indigo-600",
    accent: "bg-sky-100 text-sky-800",
    monogram: "अ",
    virtue: "Dreams & Simplicity",
    story:
      "Born in a small coastal town of Rameswaram, young Kalam sold newspapers before going to school to help his family. He dreamt of flying, but his eyesight didn't let him become a fighter pilot. Instead of giving up, he chose a bigger sky — and helped Bharat send rockets and satellites among the stars. Even as President of India, he loved being called 'Teacher'.",
    lesson:
      "Dreams are not what you see while sleeping. Dreams are what refuse to let you sleep. Dream big, work harder — and never forget the joy of teaching others."
  },
  {
    id: "rani-lakshmibai",
    name: "Rani Lakshmibai of Jhansi",
    tag: "Warrior Queen • Symbol of Bravery",
    era: "1828 – 1858",
    icon: Swords,
    palette: "from-rose-500 to-red-700",
    accent: "bg-rose-100 text-rose-800",
    monogram: "रा",
    virtue: "Fearlessness & Duty",
    story:
      "As a little girl called Manikarnika, she rode horses when other girls learned only songs. She practised sword-play, read scriptures, and could argue with elders. When her kingdom of Jhansi was in danger, she strapped her little son to her back and rode into battle herself, refusing to surrender. Even her enemies said, 'Here was a woman braver than most men.'",
    lesson:
      "It doesn't matter how small you are or how young. When you stand for the right thing, your courage can shake mountains."
  },
  {
    id: "mahatma-gandhi",
    name: "Mahatma Gandhi",
    tag: "Bapu • Apostle of Non-Violence",
    era: "1869 – 1948",
    icon: Feather,
    palette: "from-emerald-500 to-teal-600",
    accent: "bg-emerald-100 text-emerald-800",
    monogram: "गा",
    virtue: "Truth & Non-Violence",
    story:
      "As a child, Mohandas once took a small coin without permission. It troubled his heart so deeply that he wrote a letter of confession to his father, hands trembling. His father read the letter, said nothing, and quietly began to cry. That single moment taught Mohandas that truth — however painful — is the strongest force in the world. That truth walked with him for his entire life.",
    lesson:
      "Speaking the truth may feel scary, but it makes your heart light and your soul strong. Truth is a superpower."
  },
  {
    id: "chanakya",
    name: "Acharya Chanakya",
    tag: "Teacher • Kingmaker • Master of Niti",
    era: "c. 375 – 283 BCE",
    icon: Scale,
    palette: "from-violet-500 to-purple-700",
    accent: "bg-violet-100 text-violet-800",
    monogram: "चा",
    virtue: "Wisdom & Strategy",
    story:
      "Chanakya was a simple teacher in the ancient university of Takshashila. One day, a mighty king insulted him. But instead of anger, Chanakya smiled and quietly went to find a child in a small village — a bright, sharp boy named Chandragupta. He trained him with love, wisdom and discipline until that little boy became one of the greatest emperors Bharat ever knew. Chanakya himself lived in a modest hut.",
    lesson:
      "A wise teacher who believes in you can change your life forever. Choose your gurus with love and listen to them with faith."
  },
  {
    id: "bhagat-singh",
    name: "Shaheed Bhagat Singh",
    tag: "Young Revolutionary • Fearless Patriot",
    era: "1907 – 1931",
    icon: Shield,
    palette: "from-yellow-500 to-orange-600",
    accent: "bg-yellow-100 text-yellow-800",
    monogram: "भ",
    virtue: "Sacrifice & Love for Bharat",
    story:
      "As a small boy on the family farm, Bhagat Singh once asked his elders, 'Chacha ji, what are you doing?' They replied, 'Sowing seeds so that we get a good harvest.' The little boy walked into the field and began pushing tiny sticks into the ground. When asked why, he smiled and said, 'I am sowing guns — so that we get freedom.' That fire never left him.",
    lesson:
      "Even the smallest hands can plant the biggest dreams. Love for your motherland begins as a spark in childhood."
  },
  {
    id: "rani-ahilya-bai",
    name: "Ahilyabai Holkar",
    tag: "Philosopher Queen of Malwa",
    era: "1725 – 1795",
    icon: Landmark,
    palette: "from-pink-500 to-fuchsia-600",
    accent: "bg-pink-100 text-pink-800",
    monogram: "अ",
    virtue: "Service & Compassion",
    story:
      "Ahilyabai was a young village girl who loved feeding stray animals and looking after people in need. Her kindness caught the eye of Malhar Rao Holkar, who arranged for her to marry his son. Life gave her many sorrows, but she turned every sorrow into service — rebuilding temples across Bharat, digging wells, feeding travellers, and ruling her kingdom with a heart full of dharma.",
    lesson:
      "Being kind to a bird, a plant, or a stranger is not a small thing. Every act of kindness is a seed of greatness."
  }
];

const funFacts = [
  { icon: Star, fact: "The word 'zero' was gifted to the world by Aryabhata of Bharat." },
  { icon: Sparkles, fact: "Sanskrit is called the most systematic language in the world." },
  { icon: Rocket, fact: "India sent a spacecraft to Mars in its very first attempt — Mangalyaan!" },
  { icon: Trophy, fact: "The game of chess (Chaturanga) was invented in ancient Bharat." },
  { icon: Lightbulb, fact: "The idea of yoga is over 5,000 years old and still keeps the world healthy." },
  { icon: HeartHandshake, fact: "Namaste means 'the divine in me bows to the divine in you'." }
];

const KnowledgeHousePage = () => {
  const [openRiddle, setOpenRiddle] = useState(null);
  const [openJoke, setOpenJoke] = useState(null);
  const [riddleCount, setRiddleCount] = useState(12);
  const [jokeCount, setJokeCount] = useState(12);
  const [brainCount, setBrainCount] = useState(8);

  return (
    <div>
      {/* Hero */}
      <section className="pt-16 pb-14 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden" data-testid="kh-hero">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-48 h-48 border-2 border-orange-400 rounded-full" />
          <div className="absolute bottom-10 right-10 w-72 h-72 border-2 border-amber-400 rounded-full" />
          <div className="absolute top-1/2 left-1/3 w-32 h-32 border-2 border-rose-300 rounded-full" />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <BookOpen className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Gyan Ghar</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-800 mb-4">
            The <span className="text-orange-600">Knowledge House</span>
          </h1>
          <p className="font-devanagari text-2xl text-orange-700 mb-4">ज्ञान घर — कहानियाँ, हँसी और खोज का घर</p>
          <p className="text-stone-600 max-w-2xl mx-auto text-base">
            A cozy little corner filled with age-appropriate stories of Bharat's saints, kings and leaders, a Daily Value Song, a Morning Pledge, and lots of giggles — because learning at Aadi Gayanam always feels like play.
          </p>
        </div>
      </section>

      {/* Little Stories for Little Hearts — Play Group & Nursery */}
      <section className="py-16 bg-gradient-to-b from-white via-amber-50/40 to-white" data-testid="kh-little-stories">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 shadow-sm border border-orange-200 mb-3">
              <Heart className="w-4 h-4 text-orange-700 fill-orange-500" />
              <span className="text-xs font-semibold text-orange-800 tracking-widest uppercase">Ages 2 – 5 • Play Group & Nursery</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">Little Stories for Little Hearts</h2>
            <p className="text-stone-600 text-sm md:text-base">
              Fifteen bite-sized stories from the lives of India's saints, kings, philosophers and leaders — each with a beautiful value to plant in the tender minds of our smallest learners.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {littleStories.map((s) => (
              <article
                key={s.id}
                className="group rounded-3xl bg-white border border-orange-100 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 overflow-hidden flex flex-col"
                data-testid={`little-story-${s.id}`}
              >
                {s.illustration && (
                  <div className="relative w-full aspect-square overflow-hidden">
                    <img
                      src={s.illustration}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
                <div className={`relative p-5 bg-gradient-to-br ${s.palette} text-white`}>
                  <div className="absolute inset-0 opacity-15 pointer-events-none">
                    <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-white rounded-full" />
                    <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-white rounded-full" />
                  </div>
                  <div className="relative z-10 flex items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-white/95 shadow flex items-center justify-center flex-shrink-0">
                      <span className="font-devanagari text-2xl font-bold text-orange-800">{s.monogram}</span>
                    </div>
                    <div className="min-w-0">
                      <span className="inline-block bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider mb-1">
                        Story {s.id}
                      </span>
                      <h3 className="font-display text-base font-bold leading-snug">{s.title}</h3>
                    </div>
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col">
                  <div className={`inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 ${s.accent}`}>
                    <Star className="w-3 h-3" />
                    Value — {s.value}
                  </div>
                  <p className="text-sm text-stone-700 leading-relaxed mb-4 flex-1">{s.story}</p>
                  <div className="border-l-4 border-orange-500 pl-3 py-1 bg-orange-50/70 rounded">
                    <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">Moral</p>
                    <p className="text-sm text-stone-800 font-semibold">{s.moral}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Daily Value Song & Pledge */}
      <section className="py-16 bg-gradient-to-br from-orange-950 via-red-950 to-amber-950 relative overflow-hidden" data-testid="kh-song-pledge">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-56 h-56 border-2 border-yellow-300 rounded-full" />
          <div className="absolute bottom-10 right-10 w-72 h-72 border-2 border-yellow-300 rounded-full" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Daily Value Song */}
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl" data-testid="daily-value-song">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg">
                  <Music className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-orange-700 uppercase tracking-widest">Sing Together</p>
                  <h3 className="font-display text-2xl font-bold text-stone-800">Daily Value Song</h3>
                </div>
              </div>
              <div className="text-4xl mb-3">🎵</div>
              <div className="space-y-1.5 text-stone-700 leading-relaxed">
                <p>Good morning, friends, let's all say,</p>
                <p>Kind words make a happy day.</p>
                <p>Share your toys and always smile,</p>
                <p>Help each other all the while.</p>
                <p>Learn and laugh, jump and play,</p>
                <p className="font-semibold text-orange-700">Let's be kind in every way!</p>
              </div>
            </div>

            {/* Daily Value Pledge */}
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl" data-testid="daily-value-pledge">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg">
                  <HeartHandshake className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-orange-700 uppercase tracking-widest">Say It Every Morning</p>
                  <h3 className="font-display text-2xl font-bold text-stone-800">Daily Value Pledge</h3>
                </div>
              </div>
              <ul className="space-y-3">
                {pledgeLines.map((line, i) => (
                  <li key={line} className="flex items-start gap-3" data-testid={`pledge-line-${i}`}>
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-stone-800 font-semibold text-base leading-snug">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Inspirational Stories */}
      <section className="py-16 bg-white" data-testid="kh-leaders">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[10px] font-bold text-orange-700 tracking-widest uppercase mb-2">Inspirational Bharat</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">Stories of India's Greatest Leaders</h2>
            <p className="text-stone-600 text-sm md:text-base">
              Every great life once began as a small child. Read these true stories with your little one and plant the seeds of courage, character and love for Bharat.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {leaders.map((l, i) => {
              const Icon = l.icon;
              return (
                <article
                  key={l.id}
                  className="group relative overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
                  data-testid={`leader-card-${l.id}`}
                >
                  {/* Decorative header instead of stock photo */}
                  <div className={`relative h-40 bg-gradient-to-br ${l.palette} overflow-hidden`}>
                    <div className="absolute inset-0 opacity-20 pointer-events-none">
                      <div className="absolute -top-6 -left-6 w-40 h-40 border-2 border-white rounded-full" />
                      <div className="absolute -bottom-10 -right-10 w-52 h-52 border-2 border-white rounded-full" />
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border border-white rounded-full" />
                    </div>
                    <div className="relative z-10 h-full flex items-center gap-5 px-6">
                      <div className="w-20 h-20 rounded-2xl bg-white/95 shadow-lg flex items-center justify-center flex-shrink-0">
                        <span className="font-devanagari text-4xl font-bold text-orange-800">{l.monogram}</span>
                      </div>
                      <div className="text-white">
                        <span className="inline-block bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider mb-1.5">
                          {l.era}
                        </span>
                        <h3 className="font-display text-xl md:text-2xl font-bold leading-tight">{l.name}</h3>
                        <p className="text-[11px] text-white/85 mt-1">{l.tag}</p>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>

                  <div className="p-6">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 ${l.accent}`}>
                      <Star className="w-3 h-3" />
                      Virtue — {l.virtue}
                    </div>
                    <p className="text-stone-700 text-sm leading-relaxed mb-4">{l.story}</p>
                    <div className="border-l-4 border-orange-500 pl-4 py-1 bg-orange-50/60 rounded">
                      <p className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-1">The Little Lesson</p>
                      <p className="text-sm text-stone-700 italic">{l.lesson}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fun Times */}
      <section className="py-16 bg-gradient-to-br from-yellow-50 via-orange-50 to-rose-50 relative" data-testid="kh-fun-times">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-3">
              <Smile className="w-4 h-4 text-orange-600" />
              <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Fun Times</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">Giggle, Guess & Grow</h2>
            <p className="text-stone-600 text-sm md:text-base">
              A joyful corner of riddles, jokes, brain games and amazing Bharat facts. Curl up with your little one and let learning feel like laughter.
            </p>
          </div>

          {/* Fun Facts about Bharat */}
          <div className="mb-14">
            <h3 className="font-display text-xl font-bold text-stone-800 text-center mb-6">
              🌏 Wow Facts about Bharat
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {funFacts.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.fact}
                    className="p-5 rounded-2xl bg-white shadow-md border border-orange-100 hover:shadow-xl transition-all hover:-translate-y-0.5"
                    data-testid={`fun-fact-${i}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-orange-700" />
                    </div>
                    <p className="text-stone-700 text-sm leading-relaxed">{f.fact}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Riddles */}
          <div className="mb-14">
            <h3 className="font-display text-xl font-bold text-stone-800 text-center mb-2">
              🧩 Little Riddles for Little Minds
            </h3>
            <p className="text-center text-xs text-stone-500 mb-6" data-testid="riddles-count">{riddlesPool.length} playful brain teasers in the vault • showing {Math.min(riddleCount, riddlesPool.length)}</p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {riddlesPool.slice(0, riddleCount).map((r, i) => (
                <button
                  key={r.q}
                  onClick={() => setOpenRiddle(openRiddle === i ? null : i)}
                  className="text-left p-5 rounded-2xl bg-white border border-orange-100 shadow hover:shadow-lg transition-all"
                  data-testid={`riddle-${i}`}
                >
                  <p className="font-semibold text-stone-800 text-sm mb-2">Q{i + 1}. {r.q}</p>
                  <p className={`text-xs font-bold transition-all ${openRiddle === i ? "text-orange-700" : "text-orange-500"}`}>
                    {openRiddle === i ? `✨ Answer: ${r.a}` : "Tap to reveal answer →"}
                  </p>
                </button>
              ))}
            </div>
            {riddleCount < riddlesPool.length && (
              <div className="text-center mt-6">
                <Button
                  onClick={() => setRiddleCount(Math.min(riddleCount + 12, riddlesPool.length))}
                  variant="outline"
                  className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-50"
                  data-testid="riddles-more"
                >
                  Show 12 More Riddles ✨
                </Button>
              </div>
            )}
          </div>

          {/* Jokes */}
          <div className="mb-14">
            <h3 className="font-display text-xl font-bold text-stone-800 text-center mb-2">
              😄 Little Giggle Corner
            </h3>
            <p className="text-center text-xs text-stone-500 mb-6" data-testid="jokes-count">{jokesPool.length} tummy-tickling jokes in the giggle jar • showing {Math.min(jokeCount, jokesPool.length)}</p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {jokesPool.slice(0, jokeCount).map((j, i) => (
                <button
                  key={j.q}
                  onClick={() => setOpenJoke(openJoke === i ? null : i)}
                  className="text-left p-5 rounded-2xl bg-gradient-to-br from-rose-100 to-orange-100 border border-orange-200 shadow hover:shadow-lg transition-all"
                  data-testid={`joke-${i}`}
                >
                  <p className="font-semibold text-stone-800 text-sm mb-2">🤔 {j.q}</p>
                  <p className={`text-xs font-bold transition-all ${openJoke === i ? "text-rose-700" : "text-rose-500"}`}>
                    {openJoke === i ? `😂 ${j.a}` : "Tap for the giggle →"}
                  </p>
                </button>
              ))}
            </div>
            {jokeCount < jokesPool.length && (
              <div className="text-center mt-6">
                <Button
                  onClick={() => setJokeCount(Math.min(jokeCount + 12, jokesPool.length))}
                  variant="outline"
                  className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid="jokes-more"
                >
                  Show 12 More Jokes 😂
                </Button>
              </div>
            )}
          </div>

          {/* Brain Games */}
          <div>
            <h3 className="font-display text-xl font-bold text-stone-800 text-center mb-2">
              🧠 Brain Games with Bharat Flavour
            </h3>
            <p className="text-center text-xs text-stone-500 mb-6" data-testid="brain-count">{brainGamesPool.length} thinking games rooted in Bharat • showing {Math.min(brainCount, brainGamesPool.length)}</p>
            <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
              {brainGamesPool.slice(0, brainCount).map((g, i) => (
                <div
                  key={g.title + i}
                  className="p-6 rounded-2xl bg-white border border-orange-100 shadow hover:shadow-xl transition-all"
                  data-testid={`brain-game-${i}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-orange-600" />
                    <h4 className="font-display font-bold text-stone-800">{g.title}</h4>
                  </div>
                  <p className="text-sm text-stone-700 mb-3">{g.description}</p>
                  <p className="text-xs italic text-orange-700">Hint: {g.hint}</p>
                </div>
              ))}
            </div>
            {brainCount < brainGamesPool.length && (
              <div className="text-center mt-6">
                <Button
                  onClick={() => setBrainCount(Math.min(brainCount + 8, brainGamesPool.length))}
                  variant="outline"
                  className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="brain-more"
                >
                  Show 8 More Brain Games 🧠
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Closing Sanskrit */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="p-8 md:p-10 bg-gradient-to-br from-orange-950 via-red-950 to-amber-950 text-white rounded-3xl shadow-xl text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-2 left-2 w-40 h-40 border-2 border-yellow-300 rounded-full" />
              <div className="absolute bottom-2 right-2 w-52 h-52 border-2 border-yellow-300 rounded-full" />
            </div>
            <div className="relative z-10">
              <p className="font-devanagari text-2xl text-yellow-100 leading-relaxed mb-4">
                विद्या ददाति विनयं, विनयाद्याति पात्रताम्।
              </p>
              <p className="text-orange-100 italic max-w-2xl mx-auto text-sm md:text-base">
                Knowledge gives humility, humility gives worthiness — and a worthy child becomes a light for the whole world. This is the wish we hold for every little one who visits our Knowledge House.
              </p>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/admission">
              <Button className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full px-8 h-11 btn-shine" data-testid="kh-cta-admission">
                Give Your Child This Beginning <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default KnowledgeHousePage;
